<template>
  <div class="adm-table-wrap adm-scroll">
    <table class="adm-table">
      <thead>
        <tr>
          <th
            v-for="col in columns"
            :key="col.key"
            :style="colStyle(col)"
            :class="alignClass(col)"
            scope="col"
          >
            {{ col.label }}
          </th>
        </tr>
      </thead>

      <tbody v-if="loading">
        <tr>
          <td :colspan="columns.length" class="adm-table__state">Cargando…</td>
        </tr>
      </tbody>

      <tbody v-else-if="!rows.length">
        <tr>
          <td :colspan="columns.length" class="adm-table__state">
            <slot name="empty">
              <AdmEmptyState :message="emptyMessage" />
            </slot>
          </td>
        </tr>
      </tbody>

      <tbody v-else>
        <tr
          v-for="(row, index) in rows"
          :key="rowKey(row, index)"
          :class="{ 'is-selected': isSelected(row) }"
        >
          <td
            v-for="col in columns"
            :key="col.key"
            :class="alignClass(col)"
            :data-label="col.label"
          >
            <!-- Un slot por columna permite meter badges, botones o enlaces
                 sin que la tabla sepa nada de ellos. -->
            <slot :name="`cell-${col.key}`" :row="row" :value="value(row, col)" :index="index">
              {{ display(row, col) }}
            </slot>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
/**
 * Tabla de datos del panel — plan 27_09_2026 §4.2 (criterio C7).
 *
 * Había tres definiciones independientes de `.data-table` (Órdenes, Catálogo
 * y Categorías de Print3DAdmin), cada una con sus propios grises. Esta es la
 * única, y se configura por columnas:
 *
 *   columns: [{ key, label, align?, width?, format? }]
 *
 * `format` recibe (valor, fila) y devuelve el texto a pintar; para contenido
 * que no sea texto plano se usa el slot `cell-<key>` (con guion, no con dos
 * puntos: un `:` dentro del nombre obliga a argumento dinámico en cada
 * consumidor, `#[\`cell:x\`]`, que es fácil de escribir mal).
 */
import AdmEmptyState from './AdmEmptyState.vue';

export default {
  name: 'AdmTable',
  components: { AdmEmptyState },
  props: {
    columns: { type: Array, required: true },
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    emptyMessage: { type: String, default: 'No hay registros para mostrar.' },
    /** Campo identificador; si falta, se usa el índice. */
    rowId: { type: String, default: 'id' },
    /** Id de la fila resaltada (p. ej. el registro en edición). */
    selectedId: { type: [String, Number], default: null },
  },
  methods: {
    value(row, col) {
      return row?.[col.key];
    },
    display(row, col) {
      const raw = this.value(row, col);
      if (typeof col.format === 'function') return col.format(raw, row);
      return raw === null || raw === undefined || raw === '' ? '—' : raw;
    },
    rowKey(row, index) {
      return row?.[this.rowId] ?? index;
    },
    isSelected(row) {
      return this.selectedId !== null && row?.[this.rowId] === this.selectedId;
    },
    colStyle(col) {
      return col.width ? { width: col.width } : null;
    },
    alignClass(col) {
      return col.align ? `is-${col.align}` : null;
    },
  },
};
</script>

<style scoped>
.adm-table-wrap {
  width: 100%;
  /* La tabla necesita scroll horizontal propio. Efecto colateral a tener en
     cuenta: al no ser `visible` en un eje, el otro pasa a `auto`, así que
     este contenedor recorta. No pongas capas flotantes en una celda. */
  overflow-x: auto;
  background-color: var(--adm-surface);
  border: 1px solid var(--adm-border);
  border-radius: var(--adm-radius);
}

.adm-table {
  width: 100%;
  border-collapse: collapse;
  font-family: var(--font-body);
  font-size: var(--adm-font-sm);
}

.adm-table th,
.adm-table td {
  padding: 0.6rem 0.75rem;
  text-align: left;
  vertical-align: middle;
  border-bottom: 1px solid var(--adm-border);
}

.adm-table th {
  position: sticky;
  top: 0;
  z-index: var(--adm-z-table-head);
  background-color: var(--adm-surface-alt);
  color: var(--color-title);
  font-weight: 600;
  white-space: nowrap;
}

.adm-table tbody tr:last-child td { border-bottom: none; }

.adm-table tbody tr:hover { background-color: var(--adm-surface-alt); }

.adm-table tbody tr.is-selected {
  background-color: var(--adm-info-bg);
  box-shadow: inset 3px 0 0 var(--color-brand);
}

.adm-table .is-right { text-align: right; }
.adm-table .is-center { text-align: center; }

.adm-table__state {
  padding: 1.5rem 0.75rem;
  text-align: center;
  color: var(--color-text);
}
</style>
